import { toPercentages } from "../utils";
import { CONFIG, POSITIVE, NEGATIVE, NEUTRAL } from "./CONFIG";

// Pick the tag set sentiment is built from, per CONFIG.source.
export const selectSentimentItems = ({ signals = [], emotionAffect = [] }) =>
  CONFIG.source === "emotionAffect" ? emotionAffect : signals;

// Each item's polarity, per CONFIG.source:
//   "emotionAffect" → fixed emotion tagId → polarity map
//   "signals"       → signal user-lens category → polarity map
// Unlisted → neutral.
const polarityOf = (item) =>
  CONFIG.source === "emotionAffect"
    ? CONFIG.emotionPolarity[item?.tagId] || NEUTRAL
    : CONFIG.categoryPolarity[item?.category] || NEUTRAL;

// Assemble the sentiment buckets from a source-agnostic tag list (signals OR
// emotion tags — see selectSentimentItems + CONFIG.source). We roll standalone
// per-tag counts into each polarity bucket. Counts come from plain per-tag
// aggregations — current and previous-period — NOT from cooccurrence (no pairs
// needed for a sentiment rollup; a direct aggrMetrics count is the correct,
// clearer tool).
//
// One row per polarity bucket (fixed positive/negative/neutral set, kept even at
// zero), each with its total, previous-period total, and the contributing tags
// (sorted by count) for transparency. The card's headline trend is derived in
// deriveSentimentCard, so buckets carry no per-bucket trend.
export const assembleSentiment = (items, currTagCounts = {}, prevTagCounts = {}) => {
  // Contributing tags per polarity bucket, with current + previous counts.
  const byPolarity = {};
  for (const item of items || []) {
    const id = item._id;
    const total = currTagCounts[id] ?? 0;
    const prevTotal = prevTagCounts[id] ?? 0;
    (byPolarity[polarityOf(item)] ||= []).push({
      signal: item.display,
      signalId: id,
      total,
      prevTotal,
    });
  }

  return CONFIG.polarityBuckets.map((p) => {
    const members = (byPolarity[p.value] || []).sort((a, b) => b.total - a.total);
    const total = members.reduce((sum, s) => sum + s.total, 0);
    const prevTotal = members.reduce((sum, s) => sum + s.prevTotal, 0);
    return { polarity: p.display, polarityId: p.value, total, prevTotal, signals: members };
  });
};

const round = (n) => Math.round(n);
const round1 = (n) => Math.round(n * 10) / 10; // point-diff to one decimal, matching v1

// Derive the single-card view-model the v1 overview card renders, from our
// polarity buckets. This mirrors the v1 overview card EXACTLY (cardExecutor's
// computeCardSignal + formatChange), so the wording/colour/icon match:
//
//   primary metric = positive share % = positive ÷ all signal annotations × 100
//                    (the analog of v1's emotionSentimentPercent)
//   polarity       = higher_is_better → good = share > 50% (green) else red
//   changePercent  = point difference vs last period (e.g. 62% → 55% = −7)
//   value word     = New / Stable (|Δ|≤5) / Improved (good way) / Decreased (bad way)
//   change caption = "No previous data" / "No change" / "About X% up|down from last period"
export const deriveSentimentCard = (buckets = []) => {
  const by = Object.fromEntries(buckets.map((b) => [b.polarityId, b]));
  const pos = by[POSITIVE]?.total || 0;
  const neg = by[NEGATIVE]?.total || 0;
  const neu = by[NEUTRAL]?.total || 0;
  const total = pos + neg + neu;

  const prevPos = by[POSITIVE]?.prevTotal || 0;
  const prevNeg = by[NEGATIVE]?.prevTotal || 0;
  const prevNeu = by[NEUTRAL]?.prevTotal || 0;
  const prevTotal = prevPos + prevNeg + prevNeu;

  // Contributing signals per polarity → the "how is this computed" tooltip.
  const calculationBasis = {
    formula: "Positive share = positive-polarity annotations ÷ all classified annotations × 100.",
    groups: buckets.map((b) => ({ label: b.polarity, signals: b.signals.map((s) => s.signal) })),
  };

  let distribution = null;
  if (total > 0) {
    // Largest-remainder so the three segments always sum to exactly 100.
    const [positivePercent, neutralPercent, negativePercent] = toPercentages([pos, neu, neg], total);
    distribution = { total, positivePercent, neutralPercent, negativePercent };
  }

  // Primary metric: positive share % (v1's emotionSentimentPercent = positive/total).
  const currentValue = total ? round((pos / total) * 100) : 0;
  const previousValue = prevTotal ? round((prevPos / prevTotal) * 100) : 0;

  // Trend + changePercent. "New" keys off the ABSENCE of a previous period
  // (prevTotal === 0) — NOT off the metric value — so a real 0% positive share
  // with no prior period reads "New", not "Stable". With a prior period we
  // compare the positive-share point difference.
  let trend;
  let changePercent;
  if (prevTotal === 0) {
    trend = "new";
    changePercent = 0;
  } else {
    changePercent = round1(currentValue - previousValue);
    trend = Math.abs(changePercent) <= 0.5 ? "stable" : changePercent > 0 ? "rising" : "declining";
  }

  // computeCardSignal (polarity higher_is_better, isPercent): good above the
  // good-share line.
  const good = currentValue > CONFIG.goodThresholdPct;
  const signal = good ? "positive" : "negative";

  let trendDir;
  if (trend === "new") trendDir = "new";
  else if (Math.abs(changePercent) <= CONFIG.changeGatePct) trendDir = "stable";
  else trendDir = changePercent > 0 ? "improving" : "declining"; // higher_is_better → up = good

  let value;
  if (trendDir === "new") value = "New";
  else if (trendDir === "stable") value = "Stable";
  else if (trendDir === "improving") value = "Improved";
  else value = "Decreased"; // higher_is_better declining

  // formatChange.
  let change;
  if (trend === "new") change = "No previous data";
  else if (Math.abs(changePercent) <= CONFIG.changeGatePct) change = "No change";
  else change = `About ${Math.abs(changePercent)}% ${changePercent > 0 ? "up" : "down"} from last period`;

  return { label: "User Sentiment", signal, trendDir, value, change, distribution, calculationBasis };
};
